import {
  applyGoogleAdsClickIdsToPath,
  GOOGLE_ADS_CLICK_ID_PARAMS,
  readGoogleAdsClickIds,
  serializeGoogleAdsClickIds,
  trackGoogleAdsEventOnce,
  type GoogleAdsClickIds,
} from '@shared/utils/google-ads';

const STORAGE_KEY = 'amber-peak-google-ads-click';
const COOKIE_NAME = 'amber_peak_google_ads_click';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(trimmed.slice(prefix.length));
    } catch {
      return null;
    }
  }
  return null;
}

function writeCookie(value: string): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function readStoredSearch(): string {
  try {
    const fromSession = sessionStorage.getItem(STORAGE_KEY);
    if (fromSession) return fromSession;
  } catch {
    // Private mode can throw. The cookie is the fallback.
  }
  return readCookie(COOKIE_NAME) ?? '';
}

function writeStoredSearch(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Cookie still carries the click id across the Auth0 redirect.
  }
  writeCookie(value);
}

function mergedClickIds(): GoogleAdsClickIds {
  const stored = readGoogleAdsClickIds(readStoredSearch());
  const fromUrl = typeof window === 'undefined' ? {} : readGoogleAdsClickIds(window.location.search);
  return { ...stored, ...fromUrl };
}

/**
 * Remember the ad click on the prospect portal, including across the Auth0 round trip.
 * Google's cookie is on this host only after the tag sees the click id, so we keep our own copy
 * and put the id back on the URL before the tag loads.
 */
export function captureAndRestoreGoogleAdsClickIds(): void {
  if (typeof window === 'undefined') return;
  const ids = mergedClickIds();
  const serialized = serializeGoogleAdsClickIds(ids);
  if (serialized) writeStoredSearch(serialized);

  const url = new URL(window.location.href);
  let changed = false;
  for (const key of GOOGLE_ADS_CLICK_ID_PARAMS) {
    const value = ids[key];
    if (value && !url.searchParams.get(key)) {
      url.searchParams.set(key, value);
      changed = true;
    }
  }
  if (!changed) return;
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/** Record a tenant-app step once per browser. Staff impersonation does not count. */
export function trackTenantGoogleAdsOnce(memoryKey: string, eventName: string): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/impersonate')) return;
  trackGoogleAdsEventOnce(memoryKey, eventName, { site: 'portal' });
}

export function pathWithStoredGoogleAdsClickIds(path: string): string {
  const stored = readStoredSearch();
  if (!stored || typeof window === 'undefined') return path;
  return applyGoogleAdsClickIdsToPath(path, stored, window.location.origin);
}
